<script>
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Grid from '$lib/Components/Layout/Grid/Grid.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Parallax from '$lib/Components/Parallax/Parallax.svelte';
	import Awards from '$lib/PageSections/Awards/Awards.svelte';
	import FeaturedArticles from '$lib/PageSections/FeaturedArticles/FeaturedArticles.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import GradeTypes from '$lib/PageSections/GradeTypes/GradeTypes.svelte';
	import TubingColors from '$lib/PageSections/TubingColors/TubingColors.svelte';

	import ClientUsingIndowWindowMeasuringKitImage from './images/client_using_indow_window_measuring_kit.jpg';
	import MeasuringForAnEstimateStep1Image from './images/measuring_for_an_estimate_step1.png';
	import MeasuringForAnEstimateStep2Image from './images/measuring_for_an_estimate_step2.png';
	import MeasuringForAnEstimateStep3Image from './images/measuring_for_an_estimate_step3.png';
	import WindowInsertBrownTubing from './images/r_value_background.jpg';
</script>

<Section>
	<h2>How it Works</h2>
	<p>
		We make custom window inserts based on Measure by Indow, a proprietary
		software we designed to check the accuracy of window measurements. Going
		through this process ensures a precise fit, allowing us to offer you our
		Snug Fit Guarantee(™). But the whole process starts with an estimate, so you
		know how much our custom storm windows are going to cost before you decide
		to move forward.
	</p>
	<nav class="mb-8">
		<ul>
			<li>
				<a href="/about-indow/how-to-order#measure">Measure for Estimate?</a>
			</li>
			<li><a href="/about-indow/how-to-order#color">Color & Grade</a></li>
			<li><a href="/about-indow/how-to-order#laser">Laser Measuring</a></li>
			<li>
				<a href="/about-indow/how-to-order#order">Order Processing & Shipping</a
				>
			</li>
		</ul>
	</nav>
	<hr />
</Section>

<Section id="measure">
	<h2>Measuring for an Estimate on Custom Window Inserts</h2>
	<p>
		Getting an estimate is simple. It involves measuring all four sides of your
		window using a tape measure. Check out this illustrated graph:
	</p>
	<Grid columns={3} rows={3} center>
		<Grid subgrid vertical>
			<Image
				alt="window insert installation"
				src={MeasuringForAnEstimateStep1Image}
				class="w-full"
			/>
			<p>STEP 1:</p>
			<p>
				Some windows have more than one <a
					href="/resources/faq/interior-storm-window-inserts-glossary#measurement"
					>frame step</a
				>, shown above in blue and yellow. Identify the best frame step to
				install the insert.
			</p>
		</Grid>
		<Grid subgrid vertical>
			<Image
				alt="window insert installation"
				src={MeasuringForAnEstimateStep2Image}
				class="w-full"
			/>
			<p>STEP 2</p>
			<p>
				For the inserts to fit there must be at least 5/8″ of flat space all the
				way around. Measure width and round up to the nearest inch.
			</p>
		</Grid>
		<Grid subgrid vertical>
			<Image
				alt="window insert installation"
				src={MeasuringForAnEstimateStep3Image}
				class="w-full"
			/>
			<p>STEP 3</p>
			<p>
				The frame step must be unobstructed by window hardware or blinds.
				Measure the height and round up to the nearest inch.
			</p>
		</Grid>
	</Grid>
	<p>
		<a href="/how-to/get-indow-inserts">Contact us</a> for a free consultation and
		estimate.
	</p>
	<p>
		If you have an <a href="/solutions/odd-shaped-window-inserts"
			>odd-shaped window</a
		>
		or an especially large window that needs a
		<a href="/about-indow/how-to-order/mullions">mullion</a>, we will work with
		you to find the perfect solution.
	</p>
</Section>

<hr />

<Section>
	<h2>Working With a Representative</h2>
	<p>
		Once you decide to move forward, you’ll work with an Indow representative
		who can explain and help every step of the way. They’re knowledgeable about
		windows and can answer any questions you may have.
	</p>
</Section>

<hr />

<Section id="color">
	<h2>Picking the Right Grade and Colors</h2>
	<p>
		There are a range of grade types to choose from to make your windows work
		just right, whether it’s blocking UV light, increasing privacy or reducing
		noise. You can also choose from three tubing colors so the inserts blend
		into your window frames.
	</p>
	<Button href="/how-to/get-indow-inserts">GET A FREE ESTIMATE</Button>
</Section>

<GradeTypes backgroundColor="grey-light" />

<TubingColors />

<Section id="laser">
	<h2>Laser measuring and tape measurement</h2>
	<p>
		We custom make each insert to fit your window frames precisely. Once
		submitted our window measuring system checks and confirms all dimensions.
		This proprietary software has an over 99% accuracy in the over 100,000
		inserts it has assessed. This snug fit is what helps our inserts do a
		superior job of blocking drafts and noise.
	</p>
	<p>
		Houses settle over time, and our laser measurement kit will get your
		historic home the most accurate measurements possible. It also is helpful
		when measuring very tall windows or when you have a lot of windows in your
		home.
	</p>
	<p>
		For our clients who have newer homes or want a quick order process, our tape
		measurement program does not require a deposit and can get your order
		processed the fastest way possible.
	</p>
	<Image
		alt="indow insert how to order"
		src={ClientUsingIndowWindowMeasuringKitImage}
		class="w-full"
	/>
</Section>

<hr />

<Section id="order">
	<h2>How long?</h2>
	<p>
		You will receive the Laser Measuring Kit via USPS Priority Mail. We ship the
		kit the same day that your order is created, or next day depending on the
		time of day. Delivery is typically within 2-3 days of shipment. Once you’ve
		submitted your measurements and made payment on your order, it will enter
		our production queue. Our current production lead time varies based on
		current demand, order size, and a variety of other factors. You can check on
		our current lead times <a href="/estimated-lead-times">here</a>.
	</p>
	<p>
		For our tape measure orders you can receive your link to Measure By Indow
		and submit final measurements within the same day.
	</p>
</Section>

<Section>
	<h2>How Easy Is It To Install?</h2>
	<p>
		Indow inserts are easy to install and remove from your current window frames
		with no mounting brackets. Larger Indow inserts will be more difficult to
		install because of the weight and size, requiring two people. Unlike other
		storm window options that require caulking and adjustments, Indow inserts
		are custom fit to your window so they fit like a glove right out of the box.
	</p>
	<Image
		alt="indow insert how to install gif"
		src="/static/images/about-indow/how-to-order/how_to_install_window_inserts.gif"
	/>
	<p>To get started on blocking drafts and noise, fill out the form below:</p>
</Section>

<GetAFreeEstimate />
<Parallax image={WindowInsertBrownTubing} />
<Awards />
<FeaturedArticles />
